"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { SidebarBanner } from "@/components/layout/sidebar-banner";
import { useSidebarState } from "@/components/layout/sidebar-state";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useChatUnreadCount, useChatUnreadRealtime } from "@/hooks/use-chat";
import { useCurrentUser } from "@/hooks/use-current-user";
import { mainNavigation } from "@/lib/navigation";
import { cn } from "@/lib/utils";

const SERVICE_SECTION_HREFS = new Set([
  "/services/offers",
  "/services/requests",
  "/services/active",
  "/services/documents",
]);

function isNavItemActive(pathname: string, href: string) {
  if (href === "/") {
    return pathname === "/";
  }

  if (href === "/services") {
    if (pathname === "/services") {
      return true;
    }
    if (!pathname.startsWith("/services/")) {
      return false;
    }
    return ![...SERVICE_SECTION_HREFS].some(
      (section) => pathname === section || pathname.startsWith(`${section}/`),
    );
  }

  return pathname === href || pathname.startsWith(`${href}/`);
}

function UnreadBadge({
  count,
  className,
}: {
  count: number;
  className?: string;
}) {
  if (count <= 0) {
    return null;
  }

  return (
    <span
      className={cn(
        "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-semibold leading-none text-white",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function SidebarNav() {
  const pathname = usePathname();
  const { collapsed, toggle } = useSidebarState();
  const currentUserQuery = useCurrentUser();
  const user = currentUserQuery.data;
  const chatEnabled = user?.accountStatus === "active";
  const userId = chatEnabled ? user.id : null;
  useChatUnreadRealtime(userId, Boolean(userId));
  const unreadQuery = useChatUnreadCount(userId, Boolean(userId));
  const chatUnread = unreadQuery.unreadCount;

  const items = (
    <div className="space-y-6">
      {mainNavigation.map((section, index) => (
        <div key={section.title || `section-${index}`}>
          {section.title && !collapsed ? (
            <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {section.title}
            </p>
          ) : section.title && collapsed ? (
            <div className="mx-auto mb-2 h-px w-8 bg-black/[0.06]" />
          ) : null}
          <ul className="space-y-0.5">
            {section.items.map((item) => {
              const isActive = isNavItemActive(pathname, item.href);
              const unread = item.href === "/chat" ? chatUnread : 0;
              const label =
                unread > 0 && item.href === "/chat"
                  ? `${item.label}, ${unread} olvasatlan üzenet`
                  : item.label;

              return (
                <li
                  key={item.href}
                  className={collapsed ? "relative h-10" : undefined}
                >
                  <Link
                    href={item.href}
                    aria-label={label}
                    className={cn(
                      "group relative flex items-center rounded-full text-sm font-medium",
                      collapsed
                        ? "sidebar-flyout absolute top-0 left-[18px] z-10 h-10 justify-center gap-0 overflow-hidden px-[11px] hover:z-50 hover:justify-start hover:gap-3 hover:pr-4 focus-visible:z-50 focus-visible:justify-start focus-visible:gap-3 focus-visible:pr-4"
                        : "gap-3 px-3 py-2 transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : collapsed
                          ? "text-sidebar-foreground hover:bg-primary hover:text-primary-foreground"
                          : "text-sidebar-foreground hover:bg-black/[0.04] hover:text-foreground",
                    )}
                  >
                    <item.icon
                      className={cn(
                        "size-[18px] shrink-0 stroke-[1.7]",
                        isActive
                          ? "text-primary-foreground"
                          : collapsed
                            ? "text-muted-foreground group-hover:text-primary-foreground"
                            : "text-muted-foreground",
                      )}
                    />
                    {collapsed ? (
                      <>
                        <UnreadBadge
                          count={unread}
                          className="absolute top-0.5 right-0.5 h-4 min-w-4 px-1 group-hover:hidden group-focus-visible:hidden"
                        />
                        <span className="sidebar-flyout-label">
                          <span className="flex items-center gap-2">
                            {item.label}
                            <UnreadBadge count={unread} />
                          </span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        <UnreadBadge count={unread} />
                      </>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <nav className="flex h-full flex-col overflow-visible">
      {collapsed ? (
        <div className="flex-1 overflow-visible px-0 py-5">{items}</div>
      ) : (
        <ScrollArea className="flex-1 px-3 py-5">{items}</ScrollArea>
      )}

      {collapsed ? null : <SidebarBanner />}

      <div className={cn("border-t border-black/[0.04] p-2", collapsed && "flex justify-center")}>
        <Button
          type="button"
          variant="ghost"
          size={collapsed ? "icon" : "sm"}
          className={cn(
            "text-muted-foreground",
            collapsed
              ? "size-10 rounded-full hover:bg-primary hover:text-primary-foreground"
              : "w-full justify-start gap-3 rounded-full px-3",
          )}
          onClick={toggle}
          aria-label={collapsed ? "Menü kinyitása" : "Menü összecsukása"}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <PanelLeftOpen className="size-[18px] stroke-[1.7]" />
          ) : (
            <>
              <PanelLeftClose className="size-[18px] stroke-[1.7]" />
              Összecsukás
            </>
          )}
        </Button>
      </div>
    </nav>
  );
}
